<template>
  <div class="discover-wrapper">
    <div class="container">
      <div class="card">
        <div class="gallery-header">
          <p class="gallery-header-title">
            What will you discover today?
          </p>
          <div class="gallery-divider"></div>
        </div>
        <p class="filters-title">Sort by:</p>
        <div class="gallery-settings">
          <div class="gallery-filters">
            <button
                v-for="filter in filters"
                :key="filter.id"
                class="gallery-filter-button button-secondary"
                :class="{ 'button-secondary-selected': selectedFilter === filter.id }"
                @click="setNewFilter(filter.id)"
            >
              <span>{{ filter.label }}</span>
            </button>
          </div>
        </div>
        <section class="gallery">
          <article v-for="image in images" :key="image.id" class="image-card" @click="navigateToImage(image.id)">
            <img :src="image.url" :alt="image.name" loading="lazy">
            <div class="overlay">
              <div class="overlay-info">
                <h2 class="overlay-info-imagename">{{ image.name }}</h2>
                <p class="overlay-info-username">{{ image.owner_username }}</p>

              </div>
              <span>❤ {{ image.like_count }}</span>
            </div>
          </article>
        </section>
        <loading-spinner v-if="loading" color="black"></loading-spinner>
        <div ref="sentinel"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onBeforeUnmount, onMounted, ref} from "vue";
import apiService from "@/services/apiService";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import router from "@/router";

const filters = [
  {id: 'random', label: 'Random'},
  {id: 'newest', label: 'Newest'},
  {id: 'oldest', label: 'Oldest'},
  {id: 'mostLiked', label: 'Most liked'}
]

const limitStep = 10;

const selectedFilter = ref("random");
const limit = ref(10);
const skip = ref(0);

const sentinel = ref(null);
let observer = null;
const hasMore = ref(true);
const loading = ref(false);

const images = ref([]);

onMounted(async () => {
  setUpObserver();
  loading.value = true;
  await loadFeed();
  loading.value = false;
})

async function loadFeed() {
  if (selectedFilter.value === "random") {
    images.value.push(...await loadRandomFeed());
  } else {
    images.value.push(...await loadFilteredFeed());
  }
  increaseWindow();
}

async function loadRandomFeed() {
  const response = await apiService.getRandomizedFeed(limit.value)
  if (response.length === 0) {
    hasMore.value = false;
  }

  return response;
}

async function loadFilteredFeed() {
  const response = await apiService.getFilteredFeed(selectedFilter.value, limit.value, skip.value);
  if (response.length === 0) {
    hasMore.value = false;
  }

  return response;
}

async function setNewFilter(filterId) {
  selectedFilter.value = filterId;
  images.value = [];
  limit.value = limitStep;
  skip.value = 0;
  await loadFilteredFeed();
}

async function loadMore() {
  if (loading.value || !hasMore.value) {
    return;
  }

  loading.value = true;
  await new Promise(resolve => setTimeout(resolve, 1000))
  await loadFeed();

  loading.value = false;
}

function increaseWindow() {
  skip.value = limit.value;
  limit.value += limitStep;
}

async function navigateToImage(image_id) {
  await router.push(`/images/${image_id}`);
}

function setUpObserver() {
  observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      {
        root: null,
        threshold: 1
      }
  )

  if (sentinel.value) {
    observer.observe(sentinel.value);
  }
}

onBeforeUnmount(() => {
  if (observer && sentinel.value) {
    observer.unobserve(sentinel.value)
    observer.disconnect()
  }
})

</script>

<style scoped>
.discover-wrapper {
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  color: #323232;
  height: 100%;
}

.gallery-header {
  padding-top: 1rem;
  padding-bottom: 1rem;

}

.gallery-header-title {
  font-size: 2rem;
  font-weight: bold;
  margin: 0;
}

.gallery {
  columns: 4 300px;
  column-gap: 1rem;
}

.gallery-divider {
  width: 100%;
  border-bottom: 1px solid #cacaca;
  padding: 1rem 0;
}

.filters-title {
  font-size: 0.8rem;
  margin: 0.5rem 0;
}

.gallery-filters {
  display: flex;
  justify-content: start;
  align-items: center;
  margin-bottom: 2rem;
  gap: 0.5rem;
}

.gallery-filter-button {

}

.image-card {
  position: relative;
  margin-bottom: 1rem;
  break-inside: avoid;
  overflow: hidden;
  border-radius: 18px;
  background: #111;
  cursor: pointer;
}

.image-card img {
  width: 100%;
  display: block;
  transition: transform 0.3s ease;
}

.image-card:hover img {
  transform: scale(1.03);
}

.overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1rem;
  background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.85),
      rgba(0, 0, 0, 0)
  );
  color: white;
  opacity: 0;
  transition: opacity 0.25s ease;
  display: flex;
  justify-content: space-between;
  align-items: end;
}

.image-card:hover .overlay {
  opacity: 1;
}

.overlay-info-imagename {
  margin: 0;
}

.overlay-info-username {
  margin: 0;
  font-size: 0.8rem;
}
</style>